/**
 * [5.6] Drawer latéral "Détails du punch" pour la page Rapport d'heures.
 *
 * Affiche TOUTES les informations disponibles sur un punch (déjà retournées
 * par GET /api/punch/rapport) qui ne sont pas visibles dans le tableau résumé :
 * géolocalisation, alertes, pause PM, déduction proposée, strikes hors zone,
 * validation, paiements cash, etc.
 *
 * Aucune lecture/écriture supplémentaire — uniquement du rendu du payload existant.
 */
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import {
  Clock, MapPin, AlertTriangle, Coffee, TrendingDown, ShieldAlert,
  CheckCircle2, DollarSign, ChevronDown, ChevronRight, ExternalLink,
} from 'lucide-react';
import { useState } from 'react';

const fmtH = (h) => (h || 0).toFixed(2).replace('.', ',') + ' h';
const fmtMin = (m) => `${Math.round(m || 0)} min`;
const fmtDateTime = (dt) => {
  if (!dt) return '—';
  try {
    return new Date(dt).toLocaleString('fr-CA', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return dt; }
};

function Section({ icon: Icon, title, defaultOpen = false, badge = null, children, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-slate-200 last:border-b-0" data-testid={testid}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 py-3 text-left hover:bg-slate-50 -mx-4 px-4 transition-colors"
        data-testid={`${testid}-toggle`}
      >
        <div className="flex items-center gap-2.5">
          <Icon className="w-4 h-4 text-slate-600 shrink-0" />
          <span className="font-semibold text-sm text-slate-800">{title}</span>
          {badge}
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <div className="pb-4 pl-7 text-sm text-slate-700 space-y-2">{children}</div>}
    </div>
  );
}

function Row({ label, value, mono = false }) {
  return (
    <div className="flex justify-between gap-3 items-baseline">
      <span className="text-xs text-slate-500 shrink-0">{label}</span>
      <span className={`text-right ${mono ? 'font-mono text-xs' : 'text-sm'}`}>{value ?? <span className="text-slate-300">—</span>}</span>
    </div>
  );
}

export function PunchDetailsDrawer({ punch, open, onClose }) {
  if (!punch) return null;

  const horsZone = punch.geo_check?.hors_zone === true || punch.geo_check_in?.hors_zone === true || punch.geo_check_out?.hors_zone === true;
  const hasAlerts = punch.alerte_retard || punch.alerte_oubli_depunch || (punch.minutes_retard || 0) > 0;
  const pausePmApplicable = punch.pause_pm_applicable;
  const dedMin = punch.deduction_proposee_minutes || 0;
  const strikes = punch.strikes_hors_zone_count || 0;
  const cash = punch.paiements_cash || [];

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="punch-details-drawer">
        <SheetHeader className="pb-2">
          <SheetTitle className="flex items-center gap-2 text-base">
            <Clock className="w-5 h-5 text-blue-600" />
            Détails du punch
          </SheetTitle>
          <p className="text-xs text-slate-500">
            {punch.employee_nom || punch.employee_id} · {punch.punch_in_time?.slice(0, 10) || '—'}
            {punch.client_nom && <span className="block">{punch.client_nom}{punch.numero_bt ? ` · BT #${punch.numero_bt}` : ''}</span>}
          </p>
          {/* 🆕 [5.6+] Lien vers Validation pour un punch encore en attente */}
          {punch.statut === 'complete' && (
            <a
              href="/validation-heures"
              className="inline-flex items-center gap-1 mt-2 text-xs text-blue-600 hover:text-blue-800 hover:underline font-medium"
              data-testid="link-vers-validation-heures"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              Aller à Validation des heures →
            </a>
          )}
        </SheetHeader>

        <div className="mt-2 -mx-4 px-4 divide-y divide-slate-200">
          {/* 1. Punch IN / OUT — ouvert par défaut */}
          <Section icon={Clock} title="Punch IN / OUT" defaultOpen testid="section-punchio">
            <Row label="Punch IN" value={<span className="text-green-700 font-mono">{fmtDateTime(punch.punch_in_time)}</span>} />
            <Row label="Punch OUT" value={punch.punch_out_time ? <span className="text-red-600 font-mono">{fmtDateTime(punch.punch_out_time)}</span> : <Badge variant="outline" className="text-xs">En cours</Badge>} />
            <Row label="Type punch" value={<Badge variant="outline" className="text-xs">{punch.type_punch || 'chantier'}</Badge>} />
            <Row label="Heures brutes" value={fmtH(punch.heures_travaillees)} />
            <Row label="Pause prise" value={fmtMin((punch.heures_pause || 0) * 60)} />
            {/* 🆕 [Phase v13.60.6 — 2026-08-14] Fallback intelligent pour les
                punches importés pré-v13.60.6 qui n'avaient pas `pause_deduction`
                persisté. La valeur backend reste prioritaire. Aligné avec le
                fallback de ValidationHeures.jsx L 600 et RapportPointage.jsx. */}
            {(() => {
              const pauseDedH = (punch.pause_deduction != null)
                ? punch.pause_deduction
                : ((punch.heures_pause || 0) - (punch.pause_payee || 0));
              const pauseDedFinal = pauseDedH > 0 ? pauseDedH : 0;
              return pauseDedFinal > 0 ? (
                <Row label="Pause déduite" value={<span className="text-red-600">−{fmtMin(pauseDedFinal * 60)}</span>} />
              ) : null;
            })()}
            {(punch.pause_payee || 0) > 0 && (
              <Row label="Pause payée" value={<span className="text-green-600">+{fmtMin((punch.pause_payee || 0) * 60)}</span>} />
            )}
            <Row label="Heures nettes" value={<strong>{fmtH(punch.heures_nettes)}</strong>} />
            {punch.pause_ajustee_manuellement && (
              <p className="text-[11px] text-blue-700 italic mt-1">✎ Pause ajustée manuellement{punch.pause_ajustee_par ? ` par ${punch.pause_ajustee_par}` : ''}</p>
            )}
          </Section>

          {/* 2. Géolocalisation */}
          {/* 🆕 [Phase 40 — P1 #4] Ouvert par défaut + encadré coloré OK/Hors zone
              + carte iframe Google Maps pour matcher ValidationHeures */}
          <Section
            icon={MapPin}
            title="Géolocalisation"
            defaultOpen={true}
            badge={
              punch.geo_alerte === 'coords_identiques_suspect' ? (
                <Badge className="bg-red-100 text-red-700 border-red-300 text-[10px]" data-testid="badge-geo-coords-identiques">⚠️ Coords identiques in/out</Badge>
              ) : horsZone ? (
                <Badge className="bg-red-100 text-red-700 border-red-300 text-[10px]" data-testid="badge-hors-zone">⚠️ Hors zone</Badge>
              ) : null
            }
            testid="section-geo"
          >
            {(() => {
              // Encadré coloré IN
              if (!punch.punch_in_latitude && !punch.punch_out_latitude) {
                return <p className="text-xs text-amber-700">⚠️ Aucune position GPS enregistrée pour ce punch.</p>;
              }
              const renderGpsCard = (kind) => {
                const lat = kind === 'in' ? punch.punch_in_latitude : punch.punch_out_latitude;
                const lon = kind === 'in' ? punch.punch_in_longitude : punch.punch_out_longitude;
                const adr = kind === 'in' ? punch.punch_in_adresse : punch.punch_out_adresse;
                const geo = kind === 'in' ? punch.geo_check_in : (punch.geo_check_out || punch.geo_check);
                if (!lat) return null;
                const isHorsZone = geo?.hors_zone === true;
                const isDansZone = geo?.hors_zone === false;
                const reason = geo?.reason;
                const noPunchGps = reason === 'no_punch_gps';
                const presumeOk = reason === 'no_site_coords' && !!lat;
                const fauteReelle = isHorsZone || noPunchGps;
                const okFinal = isDansZone || presumeOk;
                const cls = fauteReelle ? 'bg-red-50 border-red-300'
                  : okFinal ? 'bg-emerald-50 border-emerald-200'
                  : 'bg-amber-50 border-amber-300';
                const badgeCls = fauteReelle ? 'bg-red-600 text-white'
                  : okFinal ? 'bg-emerald-600 text-white'
                  : 'bg-amber-500 text-white';
                const badgeLabel = isHorsZone ? 'Hors zone'
                  : noPunchGps ? 'GPS manquant'
                  : presumeOk ? 'Présumé OK'
                  : isDansZone ? 'OK'
                  : 'À valider';
                const icon = fauteReelle ? '🚨' : okFinal ? '✓' : '⚠️';
                return (
                  <div key={kind} className={`rounded-lg p-2.5 border ${cls} mb-2`} data-testid={`drawer-gps-card-${kind}`}>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-semibold text-slate-700">
                        {icon} Punch {kind === 'in' ? 'IN' : 'OUT'}
                      </p>
                      <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide ${badgeCls}`}>
                        {badgeLabel}
                      </span>
                    </div>
                    {adr && <p className="text-[11px] text-slate-600 mb-1">{adr}</p>}
                    <a
                      href={`https://www.google.com/maps?q=${lat},${lon}`}
                      target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-blue-600 hover:underline text-[11px] font-medium"
                      data-testid={`drawer-gps-link-${kind}`}
                    >
                      <MapPin className="w-3 h-3" />
                      Voir sur Google Maps ({lat.toFixed(5)}, {lon.toFixed(5)})
                    </a>
                  </div>
                );
              };
              // Mini-iframe Google Maps embedded (lat médian)
              const latRef = punch.punch_in_latitude || punch.punch_out_latitude;
              const lonRef = punch.punch_in_longitude || punch.punch_out_longitude;
              return (
                <>
                  {renderGpsCard('in')}
                  {punch.punch_out_time && renderGpsCard('out')}
                  {latRef && (
                    <iframe
                      title="punch-location-map"
                      data-testid="drawer-map-iframe"
                      src={`https://maps.google.com/maps?q=${latRef},${lonRef}&z=15&output=embed`}
                      width="100%" height="180" style={{ border: 0, borderRadius: 8 }}
                      loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                    />
                  )}
                </>
              );
            })()}
          </Section>

          {/* 3. Alertes & retards */}
          <Section
            icon={AlertTriangle}
            title="Alertes & retards"
            badge={hasAlerts ? <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-[10px]">⚠️</Badge> : null}
            testid="section-alertes"
          >
            <Row label="Retard détecté" value={punch.alerte_retard ? <span className="text-amber-700">⚠️ Oui</span> : 'Non'} />
            <Row label="Minutes de retard" value={(punch.minutes_retard || 0) > 0 ? `${punch.minutes_retard} min` : '—'} />
            <Row label="Oubli de dépunch" value={punch.alerte_oubli_depunch ? <span className="text-red-700">⚠️ Oui</span> : 'Non'} />
            {punch.auto_modifie && (
              <Row label="Modification auto" value={<Badge variant="outline" className="text-[10px]">par {punch.auto_modifie_par || 'employé'}</Badge>} />
            )}
          </Section>

          {/* 4. Pause PM — ouvert par défaut */}
          <Section
            icon={Coffee}
            title="Pause 15 min (après-midi)"
            defaultOpen
            badge={
              pausePmApplicable === undefined || pausePmApplicable === null ? null
              : pausePmApplicable === false ? <Badge variant="outline" className="text-[10px]">Non applicable</Badge>
              : punch.pause_pm_prise === true ? <Badge className="bg-green-100 text-green-700 border-green-300 text-[10px]" data-testid="drawer-badge-pause-pm-prise">✅ Prise</Badge>
              : punch.pause_pm_prise === false ? <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-[10px]" data-testid="drawer-badge-pause-pm-non-prise">⚠️ Non prise</Badge>
              : <Badge variant="outline" className="text-[10px]">Non déclarée</Badge>
            }
            testid="section-pause-pm"
          >
            {pausePmApplicable === false || pausePmApplicable === undefined ? (
              <p className="text-xs text-slate-500 italic">Ce punch n'est pas éligible à la déclaration pause PM (employé non terrain, propriétaire, admin, ou avant le 2026-05-25).</p>
            ) : punch.pause_pm_prise === true ? (
              <p className="text-xs text-green-700">Pause prise — aucun ajustement appliqué.</p>
            ) : punch.pause_pm_prise === false ? (
              <p className="text-xs text-amber-700">Pause NON prise — <strong>+15 min compensés</strong> ajoutés aux heures travaillées.</p>
            ) : (
              <p className="text-xs text-slate-500 italic">Aucune déclaration enregistrée (l'employé n'a pas répondu au dialog au dépunch).</p>
            )}
            {punch.pause_pm_ajustement_h ? (
              <Row label="Ajustement appliqué" value={<span className="text-amber-700">+{fmtMin((punch.pause_pm_ajustement_h || 0) * 60)}</span>} />
            ) : null}
            {punch.pause_pm_source && (
              <Row label="Source" value={<span className="text-[11px] font-mono">{punch.pause_pm_source}</span>} />
            )}
          </Section>

          {/* 5. Déduction hors-zone proposée */}
          {dedMin > 0 && (
            <Section
              icon={TrendingDown}
              title="Déduction proposée (hors zone)"
              badge={<Badge className="bg-orange-100 text-orange-700 border-orange-300 text-[10px]">{dedMin} min</Badge>}
              testid="section-deduction"
            >
              <Row label="Distance" value={punch.deduction_proposee_distance_km != null ? `${punch.deduction_proposee_distance_km.toFixed(2)} km` : '—'} />
              <Row label="Minutes proposées" value={`${dedMin} min`} />
              <p className="text-[11px] text-slate-500 italic">Calculée automatiquement lors du dépunch hors zone. Doit être validée en page « Validation des heures ».</p>
            </Section>
          )}

          {/* 6. Strikes hors zone */}
          {strikes > 0 && (
            <Section
              icon={ShieldAlert}
              title="Strikes hors zone"
              badge={<Badge className="bg-red-100 text-red-700 border-red-300 text-[10px]">{strikes}</Badge>}
              testid="section-strikes"
            >
              <p className="text-xs text-slate-600">Cet employé a accumulé <strong>{strikes} strike{strikes > 1 ? 's' : ''}</strong> pour dépunch hors zone (compteur cumulatif sur l'employé).</p>
            </Section>
          )}

          {/* 7. Validation */}
          <Section
            icon={CheckCircle2}
            title="Validation"
            badge={
              punch.statut === 'valide' ? <Badge className="bg-green-100 text-green-700 border-green-300 text-[10px]">Validé</Badge>
              : punch.statut === 'refuse' ? <Badge className="bg-red-100 text-red-700 border-red-300 text-[10px]">Refusé</Badge>
              : <Badge variant="outline" className="text-[10px]">{punch.statut || '—'}</Badge>
            }
            testid="section-validation"
          >
            <Row label="Statut" value={punch.statut} />
            <Row label="Validé par" value={punch.valide_par} />
            <Row label="Date validation" value={fmtDateTime(punch.date_validation)} />
            {punch.heures_nettes_valide != null && punch.heures_nettes_valide !== punch.heures_nettes && (
              <Row label="Heures validées" value={<span className="text-blue-700"><strong>{fmtH(punch.heures_nettes_valide)}</strong> (ajusté)</span>} />
            )}
            {punch.notes_validation && (
              <div>
                <p className="text-xs text-slate-500 mb-1">Notes :</p>
                <p className="text-xs italic bg-slate-50 border border-slate-200 rounded p-2">{punch.notes_validation}</p>
              </div>
            )}
          </Section>

          {/* 8. Paiements cash */}
          {cash.length > 0 && (
            <Section
              icon={DollarSign}
              title="Paiements cash"
              badge={<Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 text-[10px]">{cash.length}</Badge>}
              testid="section-cash"
            >
              <Row label="Heures payées cash" value={<strong>{fmtH(punch.heures_cash)}</strong>} />
              <Row label="Montant total cash" value={<strong className="text-emerald-700">{(punch.montant_cash || 0).toFixed(2)} $</strong>} />
              <Row label="Heures normales (non-cash)" value={fmtH(punch.heures_normales)} />
              <div className="mt-2 space-y-1.5">
                {cash.map((c, i) => (
                  <div key={i} className="text-[11px] bg-emerald-50 border border-emerald-200 rounded p-2">
                    <div className="flex justify-between">
                      <span>{c.heures}h · {(c.montant || 0).toFixed(2)} $</span>
                      <span className="text-slate-500">{c.date?.slice(0, 10) || ''}</span>
                    </div>
                    {c.note && <p className="text-[10px] text-slate-600 italic mt-0.5">{c.note}</p>}
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default PunchDetailsDrawer;
